import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Smartphone, QrCode, Send, ArrowLeft, CheckCircle2, XCircle, Download, Loader2, Clock } from "lucide-react";
import { api, inr, fmtDate, fmtDateTime, apiError } from "@/lib/api";
import { generateReceiptPdf } from "@/lib/receiptPdf";
import { PenguinIllustration } from "@/components/PenguinState";

const methods = [
  { id: "UPI_INTENT", label: "UPI App", desc: "Pay via any UPI app (GPay, PhonePe, Paytm)", icon: Smartphone, live: true },
  { id: "UPI_QR", label: "UPI QR", desc: "Scan a QR code to pay", icon: QrCode, live: true },
  { id: "UPI_COLLECT", label: "UPI Collect", desc: "Request sent to your VPA", icon: Send, live: true },
  { id: "NETBANKING", label: "Net Banking", desc: "Coming soon", icon: ShieldCheck, live: false },
  { id: "CARD", label: "Card", desc: "Coming soon", icon: ShieldCheck, live: false },
];

export function Payment() {
  const { state } = useLocation();
  const nvg = useNavigate();
  const loan = state?.loan;
  const [method, setMethod] = useState("UPI_INTENT");
  const [outcome, setOutcome] = useState("success");
  const [stage, setStage] = useState("idle");

  useEffect(() => { if (!loan) nvg("/loans"); }, [loan, nvg]);
  if (!loan) return null;

  const fee = 0;
  const total = loan.emi_amount + fee;

  const pay = async () => {
    setStage("processing");
    await new Promise((r) => setTimeout(r, 1400));
    try {
      const { data } = await api.post("/payments/pay", { loan_id: loan.id, method, outcome });
      if (data.status === "SUCCESS") nvg("/payment/success", { state: { payment: data } });
      else if (data.status === "PENDING") nvg("/payment/success", { state: { payment: data, pending: true } });
      else nvg("/payment/failed", { state: { payment: data, loan } });
    } catch (e) { toast.error(apiError(e)); setStage("idle"); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link to="/loans" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#0B132B]"><ArrowLeft size={15} /> Back</Link>
      <h1 className="font-display text-2xl font-bold text-[#0B132B]">Pay EMI</h1>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between"><div><p className="font-display font-bold text-[#0B132B]">{loan.lender}</p><p className="text-xs text-slate-500">{loan.loan_type}</p></div><span className="text-xs text-slate-400">Due {fmtDate(loan.next_due_date)}</span></div>
        <div className="mt-4 space-y-2 border-t border-slate-100 pt-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">EMI amount</span><span className="font-semibold text-[#0B132B]">{inr(loan.emi_amount)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Convenience fee</span><span className="font-semibold text-[#14B8A6]">{fee === 0 ? "₹0 (waived)" : inr(fee)}</span></div>
          <div className="flex justify-between border-t border-slate-100 pt-2 text-base"><span className="font-semibold text-[#0B132B]">Total payable</span><span className="font-display font-bold text-[#0B132B]">{inr(total)}</span></div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display font-bold text-[#0B132B]">Payment method</h3>
        <div className="mt-3 space-y-2">
          {methods.map((m) => (
            <button key={m.id} disabled={!m.live} onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}
              className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition ${method === m.id ? "border-[#14B8A6] bg-emerald-50" : "border-slate-200 hover:bg-slate-50"} ${!m.live ? "opacity-50" : ""}`}>
              <span className="grid h-9 w-9 place-items-center rounded-md bg-slate-100 text-[#0B132B]"><m.icon size={18} /></span>
              <span className="flex-1"><span className="block text-sm font-semibold text-[#0B132B]">{m.label}</span><span className="block text-xs text-slate-500">{m.desc}</span></span>
              {m.live && method === m.id && <CheckCircle2 size={18} className="text-[#14B8A6]" />}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-xs font-semibold text-amber-800">DEMO MODE — no real money moves. Choose a simulated outcome to preview each state:</p>
        <div className="mt-2 flex gap-2">
          {[["success", "Success"], ["pending", "Pending"], ["failed", "Failed"]].map(([v, l]) => (
            <button key={v} onClick={() => setOutcome(v)} data-testid={`outcome-${v}`} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${outcome === v ? "bg-[#0B132B] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck size={14} className="text-[#14B8A6]" /> Payments run through a gateway-ready service abstraction (Razorpay-ready).</div>

      <button onClick={pay} disabled={stage === "processing"} data-testid="confirm-pay" className="flex w-full items-center justify-center gap-2 rounded-md bg-[#14B8A6] px-4 py-3 text-sm font-semibold text-white hover:bg-[#0D9488] disabled:opacity-70">
        {stage === "processing" ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</> : <>Confirm & Pay {inr(total)}</>}
      </button>
    </div>
  );
}

export function PaymentSuccess() {
  const { state } = useLocation();
  const nvg = useNavigate();
  const p = state?.payment;
  const pending = state?.pending;
  useEffect(() => { if (!p) nvg("/dashboard"); }, [p, nvg]);
  if (!p) return null;
  const receipt = async () => {
    try {
      const { data } = await api.get(`/payments/${p.txn_id}/receipt`);
      await generateReceiptPdf(data);
      toast.success("Receipt downloaded.");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mx-auto max-w-md py-6 text-center">
      <div className={`mx-auto ${pending ? "grid h-20 w-20 place-items-center rounded-full bg-amber-100 text-amber-600" : ""}`}>
        {pending ? <Clock size={44} /> : <PenguinIllustration variant="great-job" size="inline" testid="success-penguin" />}
      </div>
      <h1 className="font-display mt-5 text-2xl font-bold text-[#0B132B]">{pending ? "Payment Pending" : "Payment Received"}</h1>
      <p className="mt-1 text-sm text-slate-500">{pending ? "Your payment is being processed. Confirmation is in progress." : "Payment submitted successfully. Confirmation is in progress."}</p>
      {!pending && (
        <div className="mx-auto mt-4 max-w-sm space-y-2">
          <div className="flex items-center justify-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="reward-pending-note">
            <Clock size={14} /> Reward Status: Pending{p.reward_amount ? ` — ₹${p.reward_amount} will be added to your ZRo Pe Wallet once your payment is confirmed` : ""}
          </div>
          <p className="text-[11px] text-slate-400">Your wallet updates automatically once the payment is confirmed.</p>
        </div>
      )}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 text-left text-sm shadow-sm">
        {[["Amount", inr(p.amount)], ["Loan", p.lender], ["Transaction ID", p.txn_id], ["Method", (p.method || "—").replace("_", " ")], ["Date & time", fmtDateTime(p.created_at)]].map(([a, b]) => (
          <div key={a} className="flex justify-between border-b border-slate-100 py-2 last:border-0"><span className="text-slate-500">{a}</span><span className="font-semibold text-[#0B132B]">{b}</span></div>
        ))}
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={receipt} data-testid="download-receipt" className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={16} /> Receipt</button>
        <Link to="/dashboard" data-testid="back-dashboard" className="flex-1 rounded-md bg-[#14B8A6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0D9488]">Back to Dashboard</Link>
      </div>
    </div>
  );
}

export function PaymentFailed() {
  const { state } = useLocation();
  const nvg = useNavigate();
  const p = state?.payment;
  const loan = state?.loan;
  useEffect(() => { if (!p) nvg("/dashboard"); }, [p, nvg]);
  if (!p) return null;
  return (
    <div className="mx-auto max-w-md py-6 text-center">
      <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-red-100 text-red-600"><XCircle size={44} /></div>
      <h1 className="font-display mt-5 text-2xl font-bold text-[#0B132B]">Payment Failed</h1>
      <p className="mt-1 text-sm text-slate-500">{p.reason || "We couldn't process your payment."}</p>
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 text-left text-sm shadow-sm">
        {[["Amount", inr(p.amount)], ["Loan", p.lender], ["Transaction ID", p.txn_id]].map(([a, b]) => (
          <div key={a} className="flex justify-between border-b border-slate-100 py-2 last:border-0"><span className="text-slate-500">{a}</span><span className="font-semibold text-[#0B132B]">{b}</span></div>
        ))}
      </div>
      <div className="mt-6 flex flex-col gap-3">
        {loan && <button onClick={() => nvg("/payment", { state: { loan } })} data-testid="retry-payment" className="rounded-md bg-[#14B8A6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0D9488]">Retry payment</button>}
        <div className="flex gap-3">
          <Link to="/loans" className="flex-1 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[#0B132B] hover:bg-slate-50">Change method</Link>
          <Link to="/support" className="flex-1 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[#0B132B] hover:bg-slate-50">Contact support</Link>
        </div>
      </div>
    </div>
  );
}
